import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Search, 
  Filter,
  Calendar,
  Download,
  User,
  FileText,
  Loader2,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { extractErrorMessage } from '@/utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_COLORS = {
  create: 'bg-[#00E87A]/20 text-[#00E87A]',
  update: 'bg-blue-500/20 text-blue-400',
  delete: 'bg-red-500/20 text-red-400',
  login: 'bg-purple-500/20 text-purple-400',
  view: 'bg-gray-600/20 text-gray-400',
  confirm: 'bg-yellow-500/20 text-yellow-400',
};

const SuperAdminAudit = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [token]);

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs || response.data || []);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(extractErrorMessage(err, 'Failed to fetch audit logs'));
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Filter logs
  const filteredLogs = logs.filter(log => {
    const matchesAction = actionFilter === 'all' || log.action?.toLowerCase().includes(actionFilter.toLowerCase());
    
    let matchesDate = true;
    if (dateFrom) {
      matchesDate = matchesDate && new Date(log.timestamp) >= new Date(dateFrom);
    }
    if (dateTo) {
      matchesDate = matchesDate && new Date(log.timestamp) <= new Date(dateTo + 'T23:59:59');
    }
    
    return matchesAction && matchesDate;
  });

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Timestamp', 'User Email', 'Action', 'Resource Type', 'Resource ID', 'IP Address'];
    const rows = filteredLogs.map(log => [
      log.timestamp,
      log.user_email || '-',
      log.action || '-',
      log.resource_type || '-',
      log.resource_id || '-',
      log.ip_address || '-'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get unique action types for filter
  const actionTypes = ['all', ...new Set(logs.map(log => log.action).filter(Boolean))];

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Audit Logs</h1>
          <p className="text-gray-400 text-sm mt-1">{logs.length} total entries</p>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={filteredLogs.length === 0}
          className="mt-4 sm:mt-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: '#00E87A' }}
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
            data-testid="action-filter"
          >
            {actionTypes.map(action => (
              <option key={action} value={action}>
                {action === 'all' ? 'All Actions' : action}
              </option>
            ))}
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-500" />
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
            placeholder="From"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
            placeholder="To"
          />
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-[#161B22] border border-gray-800 rounded-lg overflow-hidden">
        {filteredLogs.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No audit logs found</p>
            <p className="text-gray-500 text-sm mt-1">Activity logs will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Timestamp</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">User</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Action</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Resource</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Resource ID</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden lg:table-cell">IP Address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredLogs.map((log, index) => {
                  const actionColor = Object.entries(ACTION_COLORS).find(
                    ([key]) => log.action?.toLowerCase().includes(key)
                  )?.[1] || 'bg-gray-600/20 text-gray-400';
                  
                  return (
                    <tr key={log.id || index} className="hover:bg-gray-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-gray-400 text-sm">{formatDate(log.timestamp)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="text-white text-sm">{log.user_email || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${actionColor}`}>
                          {log.action || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-300 text-sm">{log.resource_type || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-gray-400 text-sm font-mono">
                          {log.resource_id ? `#${log.resource_id.slice(0, 8)}` : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-gray-400 text-sm">{log.ip_address || '-'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminAudit;
